<script lang="ts">
  import reactionService from '@services/reactionService';
  import {
    ReactionType,
    type ReactionCounts,
    type ReactionableType,
  } from '@components/reactions/types';
  import { ApiError } from '@services/api';

  import nemiTop from '@images/reactions/nemi_top.webp';
  import nemiLove from '@images/reactions/nemi_love.webp';
  import nemiScared from '@images/reactions/nemi_scared.webp';
  import nemiFacepalm from '@images/reactions/nemi_facepalm.webp';
  import nemiSad from '@images/reactions/nemi_sad.webp';

  interface Props {
    reactionableType: ReactionableType;
    reactionableId: string;
  }

  let { reactionableType, reactionableId }: Props = $props();

  let loading = $state<ReactionType | null>(null);
  let error = $state<Error | null>(null);

  let reactionCounts = $state<ReactionCounts>({
    top: 0,
    love: 0,
    scared: 0,
    facepalm: 0,
    sad: 0,
  });
  let totalReactions = $state(0);
  let selectedReaction = $state<ReactionType | null>(null);
  let showLoginPrompt = $state(false);

  const REACTIONABLE_NAMES = {
    Chapter: 'deste capítulo',
    News: 'desta notícia',
    Post: 'deste post',
  } as const;

  const reactionableName = () =>
    REACTIONABLE_NAMES[reactionableType] ?? 'deste conteúdo';

  const reactionTypes: ReactionType[] = Object.values(ReactionType);

  const reactionImages: Record<ReactionType, string> = {
    [ReactionType.Top]: nemiTop,
    [ReactionType.Love]: nemiLove,
    [ReactionType.Scared]: nemiScared,
    [ReactionType.Facepalm]: nemiFacepalm,
    [ReactionType.Sad]: nemiSad,
  };

  const reactionLabels: Record<ReactionType, string> = {
    [ReactionType.Top]: 'Incrível',
    [ReactionType.Love]: 'Adorei',
    [ReactionType.Scared]: 'Assustador',
    [ReactionType.Facepalm]: 'Inacreditável',
    [ReactionType.Sad]: 'Triste',
  };

  async function handleReaction(feeling: ReactionType): Promise<void> {
    if (loading) return;
    loading = feeling;
    showLoginPrompt = false;
    error = null;

    try {
      const response = await reactionService.react(
        reactionableType,
        reactionableId,
        feeling,
      );

      if (response.success) {
        reactionCounts = response.data.feeling_counts;
        totalReactions = response.data.total_reactions;
        selectedReaction = feeling;
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        showLoginPrompt = true;
      } else {
        error = e instanceof Error ? e : new Error(String(e));
      }
      console.error('Error reacting to chapter:', e);
    } finally {
      loading = null;
    }
  }

  async function loadReactions() {
    try {
      const response = await reactionService.getReactions(
        reactionableType,
        reactionableId,
      );

      if (response.success) {
        reactionCounts = response.data.feeling_counts;
        totalReactions = response.data.total_reactions;
        selectedReaction = response.data.user_reaction;
      }
    } catch (error) {
      console.error('Error loading reactions:', error);
    }
  }

  $effect(() => {
    loadReactions();
  });
</script>

<div class="chapter-reactions">
  <div class="reactions-header">
    <h4 class="reactions-title">
      O que você achou {reactionableName()}?
    </h4>
    <span class="total-reactions">
      {totalReactions}
      {totalReactions === 1 ? 'reação' : 'reações'}
    </span>
  </div>

  {#if error}
    <div class="error-message">
      Ocorreu um erro ao processar sua reação. Por favor, tente novamente.
    </div>
  {/if}

  {#if showLoginPrompt}
    <div class="login-prompt">
      <a href="/u/entrar">Acesse sua conta</a> para deixar sua reação
    </div>
  {/if}

  <div class="reactions-grid">
    {#each reactionTypes as reaction}
      <button
        on:click={() => handleReaction(reaction)}
        disabled={!!loading}
        class="reaction-button {selectedReaction === reaction
          ? 'selected'
          : ''}"
      >
        <div class="reaction-image">
          {#if loading === reaction}
            <div class="loading-spinner"></div>
          {:else}
            <img
              src={reactionImages[reaction]}
              alt={reactionLabels[reaction]}
            />
          {/if}
        </div>
        <span class="reaction-label">{reactionLabels[reaction]}</span>
        <span class="reaction-count">{reactionCounts[reaction] || 0}</span>
      </button>
    {/each}
  </div>
</div>

<style>
  .chapter-reactions {
    margin: 2rem 0;
    padding: 1.5rem;
    border-radius: 8px;
    background-color: #f8f9fa;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

    :global(.dark) :global(#chapter-section) & {
      background-color: #2d2d2d;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }
  }

  .reactions-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #dee2e6;

    :global(.dark) :global(#chapter-section) & {
      border-bottom-color: #4e4c4c;
    }
  }

  .reactions-title {
    margin: 0;
    font-size: 1.25rem;
    color: #343a40;

    :global(.dark) :global(#chapter-section) & {
      color: #e0e0e0;
    }
  }

  .total-reactions {
    font-size: 0.875rem;
    color: #6c757d;
    background-color: #e9ecef;
    padding: 0.5rem 1rem;
    border-radius: 20px;

    :global(.dark) :global(#chapter-section) & {
      background-color: #3d3d3d;
      color: #b0b0b0;
    }
  }

  .reactions-grid {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 1rem;
    margin: 0 auto;
  }

  .reaction-button {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0.75rem;
    border-radius: 8px;
    border: 1px solid #dee2e6;
    background-color: white;
    cursor: pointer;
    transition: all 0.2s ease;
    width: calc(33.333% - 0.667rem);
    max-width: 120px;
    min-width: 100px;

    :global(.dark) :global(#chapter-section) & {
      background-color: #3d3d3d;
      border-color: #4e4c4c;
    }
  }

  .reaction-button:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);

    :global(.dark) :global(#chapter-section) & {
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
    }
  }

  .reaction-button.selected {
    background-color: #e6f7ff;
    border-color: #1890ff;

    :global(.dark) :global(#chapter-section) & {
      background-color: #4d431a;
      border-color: #f5c109;
    }
  }

  .reaction-image {
    width: 48px;
    height: 48px;
    margin-bottom: 0.5rem;
  }

  .reaction-image img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .reaction-label {
    font-size: 0.875rem;
    margin-bottom: 0.25rem;
    color: #495057;
    text-align: center;

    :global(.dark) :global(#chapter-section) & {
      color: #e0e0e0;
    }
  }

  .reaction-count {
    font-size: 0.75rem;
    color: #6c757d;
    background-color: #f8f9fa;
    padding: 0.25rem 0.5rem;
    border-radius: 12px;

    :global(.dark) :global(#chapter-section) & {
      background-color: #2d2d2d;
      color: #b0b0b0;
    }
  }

  .loading-spinner {
    width: 100%;
    height: 100%;
    border: 3px solid #f3f3f3;
    border-top: 3px solid #1890ff;
    border-radius: 50%;
    animation: spin 1s linear infinite;

    :global(.dark) :global(#chapter-section) & {
      border-color: #3d3d3d;
      border-top-color: #f5c109;
    }
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 768px) {
    .chapter-reactions {
      padding: 1rem;
    }

    .reactions-header {
      flex-direction: column;
      gap: 0.5rem;
      text-align: center;
    }

    .reactions-grid {
      gap: 0.5rem;
      max-width: 100%;
    }

    .reaction-button {
      width: calc(50% - 0.25rem);
      padding: 0.5rem;
      max-width: 100px;
    }

    .reaction-image {
      width: 40px;
      height: 40px;
    }
  }

  .error-message {
    color: #ff4500;
    padding: 12px;
    background-color: #fff3f0;
    border-radius: 4px;
    margin-bottom: 16px;
    text-align: center;

    :global(.dark) :global(#chapter-section) & {
      background-color: #4d1f1a;
      color: #ff8f7d;
    }
  }

  .login-prompt {
    border: 1px solid #eee;
    border-radius: 8px;
    padding: 15px;
    margin-bottom: 16px;
    color: #777;
    text-align: center;

    :global(.dark) :global(#chapter-section) & {
      border-color: #4e4c4c;
      color: #ccc;
    }
  }

  .login-prompt a {
    color: #0079d3;
    text-decoration: none;

    :global(.dark) :global(#chapter-section) & {
      color: #f5c109;
    }
  }
</style>
